import {
    CancelAIGenerationParams,
    CreateAudioQueueAIParams,
    CreateAudioStudioAIParams,
    GetAudioCompositionParams,
    GetCompositionStatesParams,
    GetWaitingAudioAIParams,
    UpdateAudioCompositionParams,
} from "../types/requestInputs";
import {
    CancelAIGenerationResponse,
    CreateAudioQueueAIResponse,
    GetAudioCompositionResponse,
    GetCompositionStatesResponse,
    GetWaitingAudioAIResponse,
} from "../types/requestsOutputs";
import { fetchAPI } from "./utils";

export const createAudioQueueAI = async ({
    token,
    voiceId,
    text,
    secondVoiceId,
    isDialog,
    textTitle,
    audioSpeed,
    language,
    model,
    dialogLines,
    parentId,
}: CreateAudioQueueAIParams): Promise<CreateAudioQueueAIResponse> => {
    return fetchAPI<CreateAudioQueueAIResponse>(
        "/add-one-audio-item-queue",
        "POST",
        {
            voiceId,
            text,
            secondVoiceId,
            isDialog,
            textTitle,
            audioSpeed,
            language,
            model,
            dialogLines,
            parentId,
        },
        token,
    );
};

export const getWaitingAudioAI = async ({ token, id }: GetWaitingAudioAIParams): Promise<GetWaitingAudioAIResponse> => {
    return fetchAPI<GetWaitingAudioAIResponse>("/get-audio-ai-waiting", "POST", { audioAIId: id }, token);
};

export const cancelAIGeneration = async ({
    token,
    audioAIId,
}: CancelAIGenerationParams): Promise<CancelAIGenerationResponse> => {
    return fetchAPI<CancelAIGenerationResponse>("/cancel-ai-generation", "POST", { audioAIId }, token);
};

export const createAudioStudioAI = async ({
    token,
    text,
    audioUrl,
    contentName,
    language,
    composition,
    sourceAudioAIId,
    voiceAIId,
    voiceAISecondId,
    voiceCloneId,
    voiceCloneSecondId,
    voicePromptId,
    voicePromptSecondId,
    voiceLabel,
}: CreateAudioStudioAIParams): Promise<CreateAudioQueueAIResponse> => {
    return fetchAPI<CreateAudioQueueAIResponse>(
        "/create-one-audio-studio",
        "POST",
        {
            text,
            audioUrl,
            contentName,
            language,
            composition,
            sourceAudioAIId,
            voiceAIId,
            voiceAISecondId,
            voiceCloneId,
            voiceCloneSecondId,
            voicePromptId,
            voicePromptSecondId,
            voiceLabel,
        },
        token,
    );
};

/**
 * Sound design to preload when opening an audio in the editor. Resolved at open time,
 * never precomputed across languages.
 */
export const getAudioComposition = async ({
    token,
    audioAIId,
}: GetAudioCompositionParams): Promise<GetAudioCompositionResponse> => {
    return fetchAPI<GetAudioCompositionResponse>("/get-audio-composition", "POST", { audioAIId }, token);
};

/**
 * Which audios of a list are dressed, and which could inherit a sound design from the
 * content they were translated from. Derived server-side from the compositions.
 */
export const getCompositionStates = async ({
    token,
    audioAIIds,
}: GetCompositionStatesParams): Promise<GetCompositionStatesResponse> => {
    return fetchAPI<GetCompositionStatesResponse>("/get-composition-states", "POST", { audioAIIds }, token);
};

/**
 * Re-save a mix onto the audio it was opened from, rather than adding another row to
 * the project on every round trip through the editor.
 */
export const updateAudioComposition = async ({
    token,
    audioAIId,
    audioUrl,
    composition,
    contentName,
    language,
    voiceLabel,
    transcriptValidated,
}: UpdateAudioCompositionParams): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>(
        "/update-audio-composition",
        "POST",
        { audioAIId, audioUrl, composition, contentName, language, voiceLabel, transcriptValidated },
        token,
    );
};

export const createManualAudioAI = async ({
    token,
    text,
    audioUrl,
    textTitle,
    language,
    voiceId,
    secondVoiceId,
    isDialog,
    model,
    parentId,
}: {
    token: string;
    text: string;
    audioUrl: string;
    textTitle: string;
    language: string;
    voiceId?: string | null;
    secondVoiceId?: string | null;
    isDialog?: boolean;
    model?: string | null;
    parentId?: string | null;
}): Promise<CreateAudioQueueAIResponse> => {
    return fetchAPI<CreateAudioQueueAIResponse>(
        "/create-manual-audio-ai",
        "POST",
        {
            text,
            audioUrl,
            textTitle,
            language,
            voiceId,
            secondVoiceId,
            isDialog,
            model,
            parentId,
        },
        token,
    );
};
